
import { GoogleGenAI, Type } from "@google/genai";
import { CreativeBrief, CampaignConcept } from "../types";

const getApiKey = () => {
  // Check localStorage first for user-entered key (standard web environment)
  if (typeof window !== 'undefined') {
    const stored = window.localStorage.getItem('gemini_api_key');
    if (stored && stored.length > 5) return stored;
  }
  // Fallback to build-time env var
  return process.env.API_KEY || '';
};

const getAI = () => {
  return new GoogleGenAI({ apiKey: getApiKey() });
};

const synthesizePrompt = (brief: CreativeBrief): string => {
  const activeCount = brief.subjects.filter(s => !!s.anchorImages.frontal).length;
  
  return `
    [PHOTOGRAPHIC PRODUCTION BRIEF]
    SUBJECTS: ${activeCount} distinct individuals.
    ENVIRONMENT: ${brief.scene}.
    AESTHETIC: ${brief.vibe}, High-end documentary style.
    WARDROBE: ${brief.outfit}.
    LIGHTING: ${brief.lighting}, ${brief.lightingMood}. 
    OPTICS: ${brief.lens}, ${brief.cameraAngle}.
    REALISM: ${brief.realismTuning}, ${brief.skinTexture}.
    ADDITIONAL: ${brief.customPrompt}
  `.trim();
};

export const generateCampaignConcepts = async (brief: CreativeBrief): Promise<CampaignConcept[]> => {
  const ai = getAI();
  const synthPrompt = synthesizePrompt(brief);
  const activeCount = brief.subjects.filter(s => !!s.anchorImages.frontal).length;

  const systemInstruction = `YOU ARE AN AWARD-WINNING PHOTO-DOCUMENTARIAN. 
  Storyboard 1 campaign concept. 
  THEME: Raw Human Identity. 
  RULE: Capture the "un-perfect" reality. No smoothing, no AI-filtering. 
  FOCUS: Spatial depth, realistic skin-to-environment light bouncing, and exact facial geometry preservation.
  OUTPUT JSON ARRAY ONLY with 1 item.`;

  const parts: any[] = [{ text: `SCENE PARAMETERS: ${synthPrompt}` }];
  
  brief.subjects.forEach((subject, sIdx) => {
    const frontal = subject.anchorImages.frontal;
    if (frontal) {
      const mime = frontal.split(';')[0].split(':')[1] || 'image/jpeg';
      parts.push({ text: `REFERENCE IDENTITY FOR SUBJECT ${sIdx + 1}:` });
      parts.push({ 
        inlineData: { mimeType: mime, data: frontal.split(',')[1] } 
      });
    }
  });

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: { parts },
      config: {
        systemInstruction,
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              id: { type: Type.STRING },
              themeIndex: { type: Type.NUMBER },
              title: { type: Type.STRING },
              description: { type: Type.STRING },
              prompt: { type: Type.STRING },
            },
            required: ["id", "themeIndex", "title", "description", "prompt"],
          },
        },
      },
    });

    const text = response.text?.trim();
    if (!text) throw new Error("Concept generation returned empty.");
    return JSON.parse(text);
  } catch (error: any) {
    console.error("Concept Error:", error);
    if (error.message?.includes("Requested entity was not found")) {
      throw new Error("KEY_RESET_REQUIRED");
    }
    throw error;
  }
};

export const generateTwinImage = async (
  brief: CreativeBrief,
  conceptPrompt: string
): Promise<string> => {
  const ai = getAI();
  const isHighRes = brief.imageSize !== "1K";
  const model = isHighRes ? 'gemini-3-pro-image-preview' : 'gemini-2.5-flash-image';
  
  const parts: any[] = [];
  
  brief.subjects.forEach((subject, idx) => {
    if (subject.anchorImages.frontal) {
      const img = subject.anchorImages.frontal;
      const mime = img.split(';')[0].split(':')[1] || 'image/jpeg';
      parts.push({ text: `LITERAL IDENTITY SOURCE (SUBJECT ${idx + 1}):` });
      parts.push({ inlineData: { mimeType: mime, data: img.split(',')[1] } });
    }
  });

  const finalPrompt = `
    [PHOTOGRAPHIC TASK: BRUTALIST REALISM]
    
    IDENTITY PROTOCOL:
    - Capture every unique anatomical nuance: eye asymmetry, nose bridge curvature, specific lip shape.
    - REJECT: Do not "beautify." Do not use AI face-swap aesthetics. Do not smooth skin.
    
    OPTICAL PHYSICS (EXIF SIMULATION):
    - Camera: Phase One XF IQ4, 150MP.
    - Lens: ${brief.lens}.
    - Metadata: ISO 100, f/2.8, 1/250s.
    - Rendering: High Dynamic Range (HDR) with natural specular highlights on skin.
    - Texture: Visible micro-pores, natural skin oiliness, fine facial hairs (peach fuzz), and dermal micro-creases. 
    - No artificial denoising. Preserve subtle sensor grain for a filmic look.
    
    SCENE: ${conceptPrompt}. 
    LIGHTING: ${brief.lighting}. Use hard light or natural window light where appropriate to create realistic depth and shadow. Shadows should have a natural fall-off.
    
    FINAL IMAGE CHARACTER: This must look like a raw, high-resolution photo file from a professional session. 
    Absolutely zero "AI-glow" or "plastic sheen." 
    Subsurface scattering on skin should look wet/organic, not dry/render-like.
  `.trim();

  parts.push({ text: finalPrompt });

  try {
    const config: any = {
      imageConfig: {
        aspectRatio: brief.aspectRatio,
        imageSize: brief.imageSize || "1K"
      }
    };

    // Only Gemini 3 Pro supports googleSearch tool for image gen
    if (model === 'gemini-3-pro-image-preview') {
      config.tools = [{ googleSearch: {} }];
    }

    const response = await ai.models.generateContent({
      model,
      contents: { parts },
      config,
    });

    const candidate = response.candidates?.[0];
    if (!candidate) {
      throw new Error("Production stopped by filters. Try a more neutral setting.");
    }
    
    const imagePart = candidate.content.parts.find(p => !!p.inlineData);
    if (imagePart?.inlineData) {
      return `data:image/png;base64,${imagePart.inlineData.data}`;
    }
    
    throw new Error("Image synthesis failed to return visual data.");
  } catch (error: any) {
    console.error("Production Error:", error);
    if (error.message?.includes("Requested entity was not found")) {
      throw new Error("KEY_RESET_REQUIRED");
    }
    throw error;
  }
};
